import { CATEGORY_DEFAULTS, PACK_TERMS, plural } from "@bl/domain";
import { ChevronDown, ChevronUp, FlaskConical, RefreshCw } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { type Invoice, type InvoiceLot, type InvoicePatch, send, useChange } from "../api";
import { Empty, ErrorNote, Field } from "../components/ui";
import { showDate } from "../format";
import { PackagingDialog } from "../forms/PackagingDialog";
import { RegionInput } from "../forms/RegionInput";
import { collectSpecs, makeDrafts, type SpecDraft, SpecFields } from "../forms/SpecFields";
import { useNotify } from "../notify";
import type { ReviewState } from "./reviewAutosave";

/**
 * What a pack of a size nobody has named is probably called: the word the line itself uses ("250mL
 * Bottle"), else what its kind usually comes in, else a bag (Scott's Jam, 2026-10-07: a bottle was
 * offered as a bag, and nothing said bottle was there to pick).
 */
function packNameGuess(line: LineView): string {
  const said = PACK_TERMS.find((t) => new RegExp(`\\b${t}s?\\b`, "i").test(line.description));
  return said ?? (line.itemCategory && CATEGORY_DEFAULTS[line.itemCategory].packName) ?? "bag";
}
type PatchLines = NonNullable<InvoicePatch["lines"]>;
type PatchLots = NonNullable<PatchLines[number]["lots"]>;
interface LotDraft {
  lotId: string;
  units: string;
  lotCode: string;
  /** Hops: where they were grown and the farm. Optional, and never holding up the commit. */
  region: string;
  grower: string;
  specs: SpecDraft[];
}
export type LineView = Invoice["lines"][number];
type LotView = LineView["lots"][number];
/**
 * A card per lot the delivery will create. Where the supplier printed lot numbers there is one per
 * printed lot; where it printed none there is still one, coded with the date the delivery arrived,
 * the same default the commit would apply.
 *
 * Grain is the case that needs it: CMG prints no lot numbers at all, and without a card there is no
 * way to correct the code, nor to enter a certificate of analysis against the lot the brewer is
 * holding (Tyler, 2026-09-21).
 */
interface LotCardModel {
  /** The key the draft is held under, and the lot's id where one exists yet. */
  key: string;
  lot: LotView | null;
  lineId: string;
  billed: string;
  defaultCode: string;
}
function cardsOf(invoice: Invoice, lines: LineView[]): LotCardModel[] {
  // Annotated: the two branches infer as separate array types and will not widen `lot` on their own.
  return lines.flatMap((line): LotCardModel[] =>
    line.lots.length > 0
      ? line.lots.map((lot) => ({
          key: lot.id,
          lot,
          lineId: line.id,
          billed: line.billedUnits,
          defaultCode: lot.lotCode ?? invoice.receivedOn,
        }))
      : [
          {
            key: `${line.id}:unprinted`,
            lot: null,
            lineId: line.id,
            billed: line.billedUnits,
            defaultCode: invoice.receivedOn,
          },
        ],
  );
}
export function LotsStep({
  invoice,
  onDone,
  saved,
  onChange,
}: {
  invoice: Invoice;
  onDone: () => void;
  saved?: ReviewState["lots"];
  onChange: (lots: NonNullable<ReviewState["lots"]>) => void;
}) {
  const stockLines = invoice.lines.filter((l) => l.receivesStock && l.itemId);
  const [start] = useState(() => {
    const drafts: Record<string, LotDraft> = {};
    // A lot the brewer had typed a figure into reopens unfolded, so the half-typed figure is in sight.
    const open = new Set<string>();
    for (const line of stockLines) {
      for (const card of cardsOf(invoice, [line])) {
        // With the item's category, so a lot whose specs nobody has filled in yet still offers the
        // ones worth asking for. Without it a malt lot offered nothing at all, while hops looked
        // fine only because they inherit alpha acid (Tyler, 2026-09-21).
        const specs = makeDrafts(card.lot?.specs ?? line.itemSpecs, line.itemCategory ?? undefined);
        // Where the screen was left mid-edit, it opens exactly so: half-typed figures included.
        const left = saved?.[card.key];
        drafts[card.key] = {
          lotId: card.key,
          units: left?.units ?? card.lot?.units ?? line.billedUnits,
          lotCode: left?.lotCode ?? card.defaultCode,
          region: left?.region ?? card.lot?.region ?? "",
          grower: left?.grower ?? card.lot?.grower ?? "",
          specs: left
            ? specs.map((d) => {
                const typed = left.specs[d.property];
                return typed !== undefined ? { ...d, text: typed } : d;
              })
            : specs,
        };
        if (!left) continue;
        const typed =
          specs.some(
            (d) => left.specs[d.property] !== undefined && left.specs[d.property] !== d.text,
          ) ||
          (left.region ?? "") !== (card.lot?.region ?? "") ||
          (left.grower ?? "") !== (card.lot?.grower ?? "");
        if (typed) open.add(card.key);
      }
    }
    return { drafts, open };
  });
  const [drafts, setDrafts] = useState(start.drafts);
  const startOpen = start.open;
  const [problem, setProblem] = useState<string | null>(null);
  // Every change to the lots pass is the screen's to save, and only a change: what is on the screen
  // is compared with what was last sent, so opening the pass — or React's second start in development —
  // saves nothing, and neither does a render that changed no field.
  const lastSent = useRef<string | null>(null);
  useEffect(() => {
    const lots: NonNullable<ReviewState["lots"]> = {};
    for (const [key, d] of Object.entries(drafts)) {
      lots[key] = {
        lotCode: d.lotCode,
        units: d.units,
        region: d.region,
        grower: d.grower,
        specs: Object.fromEntries(d.specs.map((sp) => [sp.property, sp.text])),
      };
    }
    const next = JSON.stringify(lots);
    if (lastSent.current === null) {
      lastSent.current = next;
      return;
    }
    if (next === lastSent.current) return;
    lastSent.current = next;
    onChange(lots);
  }, [drafts, onChange]);
  const notify = useNotify();
  const change = useChange(send.patchInvoice, {
    onSuccess: () => {
      notify("Saved");
      onDone();
    },
  });

  const setLot = (lotId: string, patch: Partial<LotDraft>) =>
    setDrafts((all) => ({ ...all, [lotId]: { ...(all[lotId] as LotDraft), ...patch } }));

  // Sync on one lot: the number off the bag, looked up with the supplier there and then. Only that
  // card is refreshed from the answer — with whatever the brewer had typed on it, which went with the
  // request and stays theirs — so nothing typed on any other lot is lost.
  const [syncing, setSyncing] = useState<string | null>(null);
  const sync = useChange(send.syncCertificate);
  const syncLot = (card: LotCardModel, line: LineView) => {
    const draft = drafts[card.key];
    if (!draft) return;
    const collected = collectSpecs(draft.specs);
    if (collected.error) {
      setProblem(collected.error);
      return;
    }
    setProblem(null);
    setSyncing(card.key);
    const code = draft.lotCode.trim();
    sync.mutate(
      {
        invoiceId: invoice.id,
        lineId: line.id,
        body: {
          ...(card.lot ? { lotId: card.lot.id } : {}),
          lotCode: code,
          units: draft.units.trim(),
          ...(collected.specs.length ? { specs: collected.specs } : {}),
        },
      },
      {
        onSuccess: (updated) => {
          const after = updated.lines.find((l) => l.id === line.id);
          const lot =
            (card.lot && after?.lots.find((l) => l.id === card.lot?.id)) ??
            after?.lots.filter((l) => l.lotCode === code).at(-1);
          if (!lot) return;
          setDrafts((all) => {
            const { [card.key]: _replaced, ...rest } = all;
            return {
              ...rest,
              [lot.id]: {
                lotId: lot.id,
                units: lot.units,
                lotCode: lot.lotCode ?? code,
                // The lookup does not carry these, so what was typed stays.
                region: draft.region,
                grower: draft.grower,
                specs: makeDrafts(lot.specs, line.itemCategory ?? undefined),
              },
            };
          });
        },
        onSettled: () => setSyncing(null),
      },
    );
  };

  const save = () => {
    const lines: PatchLines = [];
    for (const line of stockLines) {
      const lots: PatchLots = [];
      for (const card of cardsOf(invoice, [line])) {
        const draft = drafts[card.key];
        if (!draft) continue;
        const collected = collectSpecs(draft.specs);
        if (collected.error) {
          setProblem(collected.error);
          return;
        }
        const units = draft.units.trim();
        const code = draft.lotCode.trim();
        const unitsChanged = units !== (card.lot?.units ?? line.billedUnits);
        const codeChanged = code !== card.defaultCode;
        const region = draft.region.trim();
        const grower = draft.grower.trim();
        const regionChanged = region !== (card.lot?.region ?? "");
        const growerChanged = grower !== (card.lot?.grower ?? "");
        const touched =
          unitsChanged ||
          codeChanged ||
          regionChanged ||
          growerChanged ||
          collected.specs.length > 0;
        if (!touched) continue;
        lots.push({
          // No id: a lot the document never printed, created now so it can carry what was typed
          // into it. Left alone, it is not sent at all and the commit codes it exactly the same.
          ...(card.lot ? { id: card.lot.id } : {}),
          ...(unitsChanged || !card.lot ? { units } : {}),
          ...(codeChanged || !card.lot ? { lotCode: code } : {}),
          ...(regionChanged ? { region: region || null } : {}),
          ...(growerChanged ? { grower: grower || null } : {}),
          ...(collected.specs.length ? { specs: collected.specs } : {}),
        });
      }
      if (lots.length) lines.push({ id: line.id, lots });
    }
    setProblem(null);
    if (lines.length === 0) {
      onDone();
      return;
    }
    change.mutate({ id: invoice.id, patch: { lines } });
  };

  if (stockLines.length === 0) {
    return (
      <Empty title="Nothing to confirm yet">
        <p className="muted">Match the lines to items on the first step.</p>
      </Empty>
    );
  }

  return (
    <>
      <p className="muted">
        What each lot will be recorded as. Values start from what the item says and from anything
        the supplier measured for this delivery; change any of them and this lot keeps yours.
      </p>
      {stockLines.map((line) => {
        const cards = cardsOf(invoice, [line]);
        // One lot, which is most lines: the line is one row, the item's name on it (Tyler, 2026-10-01).
        const single = cards.length === 1;
        const unnamedPack = line.pack && !line.pack.known;
        return (
          <section className={`section lot-line ${single ? "single" : ""}`} key={line.id}>
            {single ? null : (
              <div className="section-header" style={{ cursor: "default" }}>
                <h2>{line.itemName}</h2>
                <span className="aside">
                  {cards.length} {plural("lot", String(cards.length))}
                </span>
              </div>
            )}
            <div className="section-body stack">
              {/* A size nobody named is asked about in plain view; a known one is a detail. */}
              {unnamedPack || !single ? <PackNote line={line} /> : null}
              {cards.map((card) => (
                <LotCard
                  key={card.key}
                  title={single ? (line.itemName ?? undefined) : undefined}
                  packNote={single && !unnamedPack ? <PackNote line={line} /> : null}
                  startOpen={startOpen.has(card.key)}
                  card={card}
                  noun={line.pack?.name ?? null}
                  lookup={line.certificateLookup}
                  syncing={syncing === card.key}
                  syncError={
                    syncing === null && sync.variables?.lineId === line.id ? sync.error : null
                  }
                  onSync={() => syncLot(card, line)}
                  draft={drafts[card.key]}
                  onUnits={(units) => setLot(card.key, { units })}
                  onCode={(lotCode) => setLot(card.key, { lotCode })}
                  hop={line.itemCategory === "hop"}
                  onRegion={(region) => setLot(card.key, { region })}
                  onGrower={(grower) => setLot(card.key, { grower })}
                  onSpec={(property, text) =>
                    setLot(card.key, {
                      specs: (drafts[card.key]?.specs ?? []).map((d) =>
                        d.property === property ? { ...d, text } : d,
                      ),
                    })
                  }
                />
              ))}
            </div>
          </section>
        );
      })}
      {problem ? (
        <div className="form-error" role="alert">
          {problem}
        </div>
      ) : null}
      <ErrorNote error={change.error} />
      <div className="row">
        <span className="spacer" />
        <button type="button" className="button primary" onClick={save} disabled={change.isPending}>
          {change.isPending ? "Saving" : "Next"}
        </button>
      </div>
    </>
  );
}
/**
 * What the printed pack is, as the lots will be recorded: "4 × 50 lb bag". The size is the document's
 * and never changes here; the name comes from the packs this system knows, read against what the line
 * was matched to (Tyler, 2026-09-23).
 *
 * A size nothing fits is said plainly and named here, once. It is not fitted to the nearest: a 20 kg
 * sack is not a 50 lb bag, however close. Left unnamed it is still received — as a bag, or whatever
 * the item came in last — at the weight printed.
 *
 * A known size called the wrong thing (Five Star PBW's 50 lb "bag" is a bucket) is changed from here
 * in Settings' Packaging, opened over the review on the line's kind and size (Tyler, 2026-10-04).
 */
function PackNote({ line }: { line: LineView }) {
  const pack = line.pack;
  const [name, setName] = useState(() => packNameGuess(line));
  const [packaging, setPackaging] = useState(false);
  const change = useChange(send.namePack);
  if (!pack) return null;
  if (pack.known) {
    return (
      <>
        <p
          className="muted lot-note"
          title={line.packLabel ? `Printed as ${line.packLabel}` : undefined}
        >
          {line.billedUnits} × {pack.label}
          {line.itemCategory ? (
            <>
              {" "}
              <button type="button" className="link-button" onClick={() => setPackaging(true)}>
                Change
              </button>
            </>
          ) : null}
        </p>
        {line.itemCategory ? (
          <PackagingDialog
            open={packaging}
            onClose={() => setPackaging(false)}
            category={line.itemCategory}
            size={pack.size}
          />
        ) : null}
      </>
    );
  }
  return (
    <form
      className="row pack-name"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        change.mutate({ name: name.trim(), size: pack.says, category: line.itemCategory });
      }}
    >
      <span>
        <strong>{pack.label}</strong> is a size not seen before. Call it a
      </span>
      <input
        className="input"
        aria-label={`What a ${pack.label} pack is called`}
        list="pack-nouns"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button type="submit" className="button small" disabled={change.isPending || !name.trim()}>
        {change.isPending ? "Saving" : "Remember it"}
      </button>
      <datalist id="pack-nouns">
        {PACK_TERMS.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <ErrorNote error={change.error} />
    </form>
  );
}
/** Whose certificate a Sync asks for, in words. */
const LOOKUP_NAMES = { ych: "Yakima Chief Hops", cmg: "Country Malt Group" } as const;
const LOOKUP_OWNERS = { ych: "Yakima Chief Hops'", cmg: "Country Malt Group's" } as const;
function LotCard({
  title,
  packNote,
  startOpen,
  card,
  noun,
  lookup,
  syncing,
  syncError,
  onSync,
  draft,
  onUnits,
  onCode,
  hop,
  onRegion,
  onGrower,
  onSpec,
}: {
  /** The item's name, on the row itself when the line has this one lot. */
  title?: string;
  /** The pack it came in, shown with the details. */
  packNote?: ReactNode;
  /** Unfolded from the start: the brewer had typed into it before. */
  startOpen?: boolean;
  card: LotCardModel;
  noun: string | null;
  lookup: "ych" | "cmg" | null;
  syncing: boolean;
  syncError: unknown;
  onSync: () => void;
  draft: LotDraft | undefined;
  onUnits: (units: string) => void;
  onCode: (lotCode: string) => void;
  /** A lot of hops, which is asked where it was grown and by whom. */
  hop: boolean;
  onRegion: (region: string) => void;
  onGrower: (grower: string) => void;
  onSpec: (property: string, text: string) => void;
}) {
  const { lot, billed } = card;
  const short = draft && draft.units.trim() !== (lot?.units ?? billed);
  const printed = lot?.lotCode != null;
  // Folded to one row, as most lots need nothing more (Tyler, 2026-10-01); opened by the arrow, and
  // open of itself where there is something to read: a count made short, a lookup's answer, an error.
  const [opened, setOpened] = useState(startOpen ?? false);
  const worthSeeing = !!short || !!lot?.certificate || !!syncError;
  const open = opened || worthSeeing;
  return (
    <div className={`lot-card ${open ? "open" : ""}`}>
      <div className="row lot-card-head">
        {title ? <strong className="lot-item">{title}</strong> : null}
        <label className="row lot-code">
          <span className="muted">Lot</span>
          <input
            className="input"
            aria-label="Lot number"
            value={draft?.lotCode ?? card.defaultCode}
            onChange={(e) => onCode(e.target.value)}
          />
        </label>
        {lookup ? (
          <button
            type="button"
            className="button small"
            disabled={syncing || !isLotNumber(draft?.lotCode ?? card.defaultCode)}
            onClick={onSync}
            title={`Fill this lot in from ${LOOKUP_OWNERS[lookup]} certificate of analysis`}
          >
            <RefreshCw size={14} aria-hidden className={syncing ? "spinning" : undefined} />
            {syncing ? "Syncing" : "Sync"}
          </button>
        ) : null}
        {lot?.cropYear ? <span className="chip">{lot.cropYear} crop</span> : null}
        <span className="spacer" />
        <label className="row lot-count">
          <span>Arrived</span>
          <input
            className="input num"
            inputMode="decimal"
            value={draft?.units ?? lot?.units ?? billed}
            onChange={(e) => onUnits(e.target.value)}
          />
          <span className="muted">
            of {billed} {noun ? `${plural(noun, billed)} ` : ""}billed
          </span>
        </label>
        <button
          type="button"
          className="icon-button lot-toggle"
          aria-expanded={open}
          aria-label={open ? "Hide lot details" : "Show lot details"}
          title={
            open
              ? "Hide lot details"
              : hop
                ? "Lot details: region, grower and specs"
                : "Lot details: specs and notes"
          }
          disabled={worthSeeing}
          onClick={() => setOpened((o) => !o)}
        >
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      {open ? (
        <div className="lot-details">
          {packNote}
          <CertificateNote certificate={lot?.certificate ?? null} lookup={lookup} />
          <PrintedCertificateNote lot={lot} />
          <ErrorNote error={syncError} />
          {printed ? null : (
            <p className="muted lot-note">
              {lookup
                ? `The supplier printed no lot number, so this is coded with the date it came in. Type the number off the bag and press Sync to fill it in from ${LOOKUP_OWNERS[lookup]} certificate.`
                : "The supplier printed no lot number, so this is coded with the date it came in. Change it if the bag says otherwise."}
            </p>
          )}
          {short ? (
            <p className="muted lot-note">
              Counting a lot short is how you say which one came up light. The line follows from its
              lots.
            </p>
          ) : null}
          {hop && draft ? (
            <div className="form-grid">
              <Field
                label="Region"
                hint="Where they were grown. Left blank, the item number's, if it says."
              >
                <RegionInput value={draft.region} onChange={onRegion} />
              </Field>
              <Field label="Grower" hint="The farm, if the bag or the supplier says.">
                <input
                  className="input"
                  value={draft.grower}
                  onChange={(e) => onGrower(e.target.value)}
                />
              </Field>
            </div>
          ) : null}
          {draft && draft.specs.length > 0 ? (
            <SpecFields drafts={draft.specs} onChange={onSpec} />
          ) : (
            <p className="muted lot-note">No specs recorded for this item yet.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
/** A lot code a supplier could look up: something typed, and not the received date standing in. */
const isLotNumber = (code: string) => {
  const trimmed = code.trim();
  return trimmed !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed);
};
/**
 * What the supplier said about this lot's number, the last time it was asked. The figures themselves
 * are in the fields below, each saying it came from the certificate; this says whether there was one,
 * so an empty field is never mistaken for a lookup that found nothing to say (Tyler, 2026-09-24).
 */
function CertificateNote({
  certificate,
  lookup,
}: {
  certificate: InvoiceLot["certificate"];
  lookup: "ych" | "cmg" | null;
}) {
  if (!certificate || !lookup) return null;
  const who = LOOKUP_NAMES[certificate.source];
  const whose = LOOKUP_OWNERS[certificate.source];
  const when = showDate(certificate.fetchedAt.slice(0, 10));
  if (certificate.status === "found") {
    return (
      <p className="lot-note certificate-note found">
        <FlaskConical size={13} aria-hidden /> From {whose} certificate, {when}
        {certificate.description ? ` — ${certificate.description}` : ""}.
        {certificate.provisional ? " Provisional: the lab may still revise these figures." : ""}
      </p>
    );
  }
  if (certificate.status === "not_found") {
    return (
      <p className="lot-note certificate-note">
        <FlaskConical size={13} aria-hidden /> {who} has no certificate for {certificate.lotCode}{" "}
        yet. Check the number, or enter the figures from the bag.
      </p>
    );
  }
  return (
    <p className="lot-note certificate-note failed">
      <FlaskConical size={13} aria-hidden /> Could not reach {who} ({when}). Sync to try again, or
      enter the figures from the bag.
    </p>
  );
}
/**
 * Figures read off a certificate the supplier printed with the invoice — Yakima's last page — rather
 * than looked up anywhere. Said once above the fields, as a lookup's are, so the brewer knows the
 * numbers are the lab's for this lot and not the catalog's for the variety.
 */
function PrintedCertificateNote({ lot }: { lot: LotView | null }) {
  const printed = lot?.specs.find((s) => s.source?.name.endsWith("certificate on the invoice"));
  if (!printed?.source || lot?.certificate?.status === "found") return null;
  return (
    <p className="lot-note certificate-note found">
      <FlaskConical size={13} aria-hidden /> From the certificate printed on the invoice, issued{" "}
      {showDate(printed.source.asOf)}.
    </p>
  );
}
